import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import ProfileAttributes from "../components/ProfileAttributes";
import { 
  ArrowLeft, MapPin, BadgeCheck, Heart, MessageCircle, 
  Star, X, ChevronLeft, ChevronRight, Loader2
} from "lucide-react";

import { API } from "../config";
import { tx } from "../lib/tx";

export default function ProfilePreview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [loadedPhotos, setLoadedPhotos] = useState({});
  const [loadingPhotos, setLoadingPhotos] = useState(true);

  // Charger les photos uploadées
  useEffect(() => {
    const loadPhotos = async () => {
      if (!user?.photos?.length) {
        setLoadingPhotos(false);
        return;
      }

      const token = localStorage.getItem("session_token");
      const loaded = {};

      for (const photo of user.photos) {
        if (photo.startsWith("/api/photos/")) {
          try {
            const response = await fetch(`${API}/photos/${photo.replace("/api/photos/", "")}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
              const data = await response.json();
              loaded[photo] = data.data;
            }
          } catch (err) {
            console.error("Error loading photo:", err);
          }
        } else {
          loaded[photo] = photo;
        }
      }

      setLoadedPhotos(loaded);
      setLoadingPhotos(false);
    };

    loadPhotos();
  }, [user]);

  const photos = user?.photos || [];
  const mainPhoto = user?.main_photo || photos[0];
  
  // Réorganiser les photos pour mettre la principale en premier
  const sortedPhotos = mainPhoto 
    ? [mainPhoto, ...photos.filter(p => p !== mainPhoto)]
    : photos;

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % sortedPhotos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + sortedPhotos.length) % sortedPhotos.length);
  };

  const currentPhoto = sortedPhotos[currentPhotoIndex];
  const photoSrc = loadedPhotos[currentPhoto] || user?.picture;

  return (
    <div className="min-h-screen bg-background" data-testid="profile-preview-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-surface h-16 flex items-center px-4 gap-3">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(-1)}
          className="text-white"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>
        <h1 className="font-heading text-xl font-semibold text-white flex-1">
          {tx("Aperçu de mon profil")}
        </h1>
      </header>

      {/* Banner indiquant que c'est un aperçu */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-primary/90 text-white text-center py-2 text-sm">
        {tx("👁️ Voici comment les autres membres voient votre profil")}
      </div>

      <div className="pt-28 pb-24">
        {/* Photo principale avec navigation */}
        <div className="relative aspect-[3/4] max-h-[70vh]">
          {loadingPhotos ? (
            <div className="absolute inset-0 flex items-center justify-center bg-muted">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          ) : photoSrc ? (
            <img
              src={photoSrc}
              alt={user?.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/40">
              <span className="text-8xl text-white/50">{user?.name?.[0]}</span>
            </div>
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" />

          {/* Photo indicators */}
          {sortedPhotos.length > 1 && (
            <div className="absolute top-4 left-4 right-4 flex gap-1">
              {sortedPhotos.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 flex-1 rounded-full transition-all ${
                    index === currentPhotoIndex ? "bg-white" : "bg-white/40"
                  }`}
                />
              ))}
            </div>
          )}

          {/* Navigation arrows */}
          {sortedPhotos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 flex items-center justify-center text-white"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/30 flex items-center justify-center text-white"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* User info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-end gap-2 mb-2">
              <h1 className="font-heading text-4xl font-bold text-white">
                {user?.name}
              </h1>
              {user?.age && (
                <span className="text-3xl text-white/90">{user.age}</span>
              )}
              {user?.is_verified && (
                <BadgeCheck className="w-7 h-7 text-green-500 mb-1" />
              )}
            </div>

            {user?.location && (
              <div className="flex items-center gap-1 text-white/80 mb-3">
                <MapPin className="w-4 h-4" />
                <span>{user.location}</span>
              </div>
            )}

            {/* Interests */}
            {user?.interests?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {user.interests.slice(0, 5).map((interest, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm"
                  >
                    {interest}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bio section */}
        <div className="px-6 py-6 space-y-6">
          {user?.bio && (
            <div>
              <h2 className="text-lg font-semibold text-white mb-2">{tx("À propos")}</h2>
              <p className="text-muted-foreground leading-relaxed">{user.bio}</p>
            </div>
          )}

          {/* Profile Attributes - Informations complémentaires */}
          <ProfileAttributes user={user} language="fr" />

          {user?.looking_for && (
            <div>
              <h2 className="text-lg font-semibold text-white mb-2">{tx("Je recherche")}</h2>
              <p className="text-muted-foreground">{user.looking_for}</p>
            </div>
          )}

          {/* All interests */}
          {user?.interests?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-white mb-2">{tx("Centres d'intérêt")}</h2>
              <div className="flex flex-wrap gap-2">
                {user.interests.map((interest, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-muted rounded-full text-white text-sm"
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Fake action buttons (disabled, just for preview) */}
        <div className="fixed bottom-0 left-0 right-0 p-4 glass-surface">
          <div className="flex items-center justify-center gap-4">
            <div className="w-14 h-14 rounded-full bg-muted/50 flex items-center justify-center opacity-50">
              <X className="w-8 h-8 text-muted-foreground" />
            </div>
            <div className="w-16 h-16 rounded-full bg-primary/50 flex items-center justify-center opacity-50">
              <Heart className="w-9 h-9 text-white" />
            </div>
            <div className="w-14 h-14 rounded-full bg-muted/50 flex items-center justify-center opacity-50">
              <Star className="w-8 h-8 text-muted-foreground" />
            </div>
          </div>
          <p className="text-center text-muted-foreground text-xs mt-2">
            {tx("Aperçu des boutons d'action (désactivés)")}
          </p>
        </div>
      </div>
    </div>
  );
}
